/**
 * @fileoverview Status/role pill atom.
 * @description A small rounded label used for status dots, role tags, and priority/rating chips
 * across list and table views. Visual treatment is a closed variant prop mapping to one complete
 * background/text color pair (from the semantic status tokens in tokens/colors.css) rather than an
 * overridable className, matching Button/Input/IconButton. `dot` renders a small colored dot ahead
 * of the label, for status columns (e.g. "Active"/"Inactive") that pair a dot with text.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface BadgeProps {
  children: React.ReactNode
  variant?: 'neutral' | 'info' | 'success' | 'warning' | 'danger' | undefined
  dot?: boolean | undefined
  'data-testid'?: string | undefined
}

const VARIANT_CLASS_NAMES: Record<NonNullable<BadgeProps['variant']>, string> = {
  neutral: 'bg-surface-muted text-slate',
  info: 'bg-info-bg text-info',
  success: 'bg-success-bg text-success',
  warning: 'bg-status-warning-bg text-status-warning',
  danger: 'bg-danger-bg text-danger',
}

const DOT_CLASS_NAMES: Record<NonNullable<BadgeProps['variant']>, string> = {
  neutral: 'bg-slate',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-status-warning',
  danger: 'bg-danger',
}

/**
 * @component
 * @param {BadgeProps} props - The label content plus its color variant.
 * @param {React.ReactNode} props.children - The badge's label text.
 * @param {'neutral' | 'info' | 'success' | 'warning' | 'danger'} [props.variant] - Which semantic
 * color pair to use. Defaults to "neutral".
 * @param {boolean} [props.dot] - Shows a small colored dot ahead of the label. Off by default.
 * @param {string} [props['data-testid']] - QA locator for the pill.
 */
function Badge({ children, variant = 'neutral', dot = false, 'data-testid': testId }: BadgeProps) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-[5px] px-2.5 py-1 text-xs font-semibold ${VARIANT_CLASS_NAMES[variant]}`}
    >
      {dot && <span className={`h-1.5 w-1.5 rounded-full ${DOT_CLASS_NAMES[variant]}`} />}
      {children}
    </span>
  )
}

export default Badge
